// Unpublished Work © 2023-2024 Deere & Company.
import { InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Grid,
  SxProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { surchargeRenderToggleOn } from "./surchargeConditionHelper";
import { CanadaTaxDetails, PalletCharges } from "../../api/invoices/interfaces";
import { ConfigContext } from "../../context/ConfigContext";
import { DealerAccountContext } from "../../context/DealerAccountContext";
import NoDataAvailableError from "../../pages/orders/components/NoDataAvailableError/NoDataAvailableError";
import {
  CANADA_DEALER_LOCATION,
  US_DEALER_LOCATION,
} from "../../utils/constants";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";

const DetailedInvoiceSummary = ({
  currency,
  partsSubtotal,
  discounts,
  surcharges,
  freightCharges,
  environmentFees,
  canadaTaxDetails,
  palletCharges,
  flashPlusCharges,
  tax,
  total,
  isDataPresent,
  showLoader,
  sx,
}: {
  currency?: string;
  partsSubtotal?: number;
  discounts?: number;
  surcharges?: number;
  freightCharges?: number;
  environmentFees?: number;
  canadaTaxDetails?: CanadaTaxDetails;
  palletCharges?: PalletCharges;
  flashPlusCharges?: number;
  tax?: number;
  total?: number;
  isDataPresent: boolean;
  showLoader: boolean;
  sx?: SxProps;
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation("invoices");
  const { dealerLocation } = useContext(DealerAccountContext);
  const isUSDealer = dealerLocation === US_DEALER_LOCATION;
  const isCADealer = dealerLocation === CANADA_DEALER_LOCATION;

  const { ENABLE_INVOICE_DETAILS_PORTLAND_SURCHARGE } =
    useContext(ConfigContext);

  const PORTLAND_SURCHARGE_TOGGLE_ON_RENDER = surchargeRenderToggleOn(
    isUSDealer,
    isCADealer,
    ENABLE_INVOICE_DETAILS_PORTLAND_SURCHARGE
  );

  const conversion = (value: number | undefined) =>
    value?.toLocaleString(i18n.language?.replace("_", "-"), {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) ?? "";
  return (
    <Card sx={sx}>
      <CardContent>
        <Typography
          sx={{
            fontWeight: "600",
            color: theme.tokens.FuelColorTextPrimary,
          }}
          variant="h4"
          data-testid="detailed-invoice-summary"
        >
          {t("invoiceDetails.label.detailedInvoiceSummary")}
          {currency && currency.trim().length > 0 ? ` (${currency})` : ""}
        </Typography>

        {showLoader && <LoadingIndicator />}

        {!showLoader &&
          (isDataPresent ? (
            <Box paddingTop={theme.tokens.FuelSpacing4}>
              <Grid container spacing={2} rowGap="16px">
                <Grid size={6}>
                  <Typography
                    sx={{ color: theme.tokens.FuelColorTextTertiary }}
                    data-testid="invoice-parts-subtotal"
                  >
                    {t("invoiceDetails.label.partsSubtotal")}:
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography
                    sx={{ color: theme.tokens.FuelColorTextPrimary }}
                    data-testid="invoice-parts-subtotal-value"
                    align="right"
                  >
                    {conversion(partsSubtotal)}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography
                    sx={{ color: theme.tokens.FuelColorTextTertiary }}
                  >
                    {t("invoiceDetails.label.discounts")}:
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography
                    sx={{ color: theme.tokens.FuelColorTextPrimary }}
                    data-testid="invoice-parts-discounts-value"
                    align="right"
                  >
                    {conversion(discounts)}
                  </Typography>
                </Grid>
                {surcharges !== undefined && (
                  <>
                    <Grid size={6}>
                      <Typography
                        sx={{ color: theme.tokens.FuelColorTextTertiary }}
                      >
                        {t("invoiceDetails.label.surcharges")}:
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography
                        sx={{ color: theme.tokens.FuelColorTextPrimary }}
                        data-testid="invoice-parts-surcharges-value"
                        align="right"
                      >
                        {PORTLAND_SURCHARGE_TOGGLE_ON_RENDER
                          ? conversion(flashPlusCharges)
                          : conversion(surcharges)}
                      </Typography>
                    </Grid>
                  </>
                )}

                {PORTLAND_SURCHARGE_TOGGLE_ON_RENDER &&
                  palletCharges !== undefined && (
                    <>
                      <Grid size={4}>
                        <Typography
                          sx={{ color: theme.tokens.FuelColorTextTertiary }}
                        >
                          {t("invoiceDetails.label.palletCharges")}:
                        </Typography>
                      </Grid>
                      <Grid size={1}>
                        <div>
                          {palletCharges?.totalPalletCharge !== 0 && (
                            <Tooltip
                              data-testid="invoice-parts-palletCharges-tooltip"
                              placement="top"
                              PopperProps={{
                                sx: {
                                  ".MuiTooltip-tooltipArrow": {
                                    backgroundColor:
                                      theme.tokens.FuelColorGray1000,
                                  },
                                },
                              }}
                              title={
                                <ul
                                  style={{
                                    margin: 0,
                                    padding: "0 1rem",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {palletCharges?.palletCharges?.map(
                                    (charge, index) => (
                                      <li key={`tooltip-${index + 1}`}>{`${
                                        charge.description
                                      } - ${conversion(
                                        charge.palletCharge
                                      )}`}</li>
                                    )
                                  )}
                                </ul>
                              }
                            >
                              <InfoOutlined
                                sx={{
                                  color: theme.tokens.FuelColorAlertInfoIcon,
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </Grid>

                      <Grid size={7}>
                        <Typography
                          sx={{ color: theme.tokens.FuelColorTextPrimary }}
                          data-testid="invoice-parts-total-palletCharges-value"
                          align="right"
                        >
                          {conversion(palletCharges?.totalPalletCharge)}
                        </Typography>
                      </Grid>
                    </>
                  )}

                {PORTLAND_SURCHARGE_TOGGLE_ON_RENDER && isCADealer && (
                  <>
                    <Grid size={6}>
                      <Typography
                        sx={{ color: theme.tokens.FuelColorTextTertiary }}
                      >
                        {t("invoiceDetails.label.environmentFees")}:
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography
                        sx={{ color: theme.tokens.FuelColorTextPrimary }}
                        data-testid="invoice-parts-environmentalFee-value"
                        align="right"
                      >
                        {conversion(environmentFees)}
                      </Typography>
                    </Grid>
                  </>
                )}

                {PORTLAND_SURCHARGE_TOGGLE_ON_RENDER &&
                  freightCharges !== undefined && (
                    <>
                      <Grid size={6}>
                        <Typography
                          sx={{ color: theme.tokens.FuelColorTextTertiary }}
                        >
                          {t("invoiceDetails.label.freightCosts")}:
                        </Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography
                          sx={{ color: theme.tokens.FuelColorTextPrimary }}
                          data-testid="invoice-parts-freightCosts-value"
                          align="right"
                        >
                          {conversion(freightCharges)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                <Grid size={3}>
                  <Typography
                    sx={{ color: theme.tokens.FuelColorTextTertiary }}
                  >
                    {t("invoiceDetails.label.tax")}:
                  </Typography>
                </Grid>
                {canadaTaxDetails && (
                  <Grid size={1}>
                    <div>
                      <Tooltip
                        data-testid="invoice-parts-canadaTaxDetails-tooltip"
                        placement="top"
                        PopperProps={{
                          sx: {
                            ".MuiTooltip-tooltipArrow": {
                              backgroundColor: theme.tokens.FuelColorGray1000,
                            },
                          },
                        }}
                        title={
                          <ul
                            style={{
                              margin: 0,
                              padding: "0 1rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <li key="tooltip">
                              {t("invoiceDetails.label.gst")}-
                              {` ${conversion(canadaTaxDetails?.canadaGST)}`}
                            </li>
                            <li key="tooltip">
                              {t("invoiceDetails.label.pst")}-{" "}
                              {`${conversion(canadaTaxDetails?.canadaPST)}`}
                            </li>
                          </ul>
                        }
                      >
                        <InfoOutlined
                          sx={{
                            color: theme.tokens.FuelColorAlertInfoIcon,
                          }}
                        />
                      </Tooltip>
                    </div>
                  </Grid>
                )}
                {!canadaTaxDetails && <Grid size={1} />}
                <Grid size={8}>
                  <Typography
                    sx={{ color: theme.tokens.FuelColorTextPrimary }}
                    align="right"
                    data-testid="invoice-parts-tax-value"
                  >
                    {conversion(tax)}
                  </Typography>
                </Grid>

                <Grid size={6}>
                  <Typography
                    sx={{
                      fontWeight: "600",
                      color: theme.tokens.FuelColorTextPrimary,
                    }}
                    variant="h4"
                  >
                    {t("invoiceDetails.label.total")}:
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography
                    align="right"
                    sx={{
                      fontWeight: "600",
                      color: theme.tokens.FuelColorTextPrimary,
                    }}
                    variant="h4"
                    data-testid="invoice-parts-total-value"
                  >
                    {conversion(total)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <NoDataAvailableError />
          ))}
      </CardContent>
    </Card>
  );
};

export default DetailedInvoiceSummary;
